"use client";

import { useState, useRef, useEffect } from "react";
import { Button, Col, Modal, Row, Form, Alert } from "reactstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { addRates, updateRates } from "../../../store/roomsrates/actions";
import axios from "axios";

const schema = yup.object().shape({
  roomtype: yup.string().max(100).required(),
  roomfloor: yup.string().max(100).required(),
  roomtypear: yup.string().max(100).required(),
  roomfloorar: yup.string().max(100).required(),
  syp: yup.string().max(100).required(),
  usd: yup.string().max(100).required(),
});

export default function AddRoomRate({
  modal_center,
  tog_center,
  setmodal_center,
  data,
}) {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      roomtype: data.roomtype || "",
      roomfloor: data.roomfloor || "",
      roomtypear: data.roomtypear || "",
      roomfloorar: data.roomfloorar || "",
      syp: data.syp || "",
      usd: data.usd || "",
    },
  });

  // Fetch existing images when editing
  useEffect(() => {
    if (modal_center && Object.keys(data).length !== 0) {
      // If data already has images array, use it directly
      if (data.images && data.images.length > 0) {
        console.log("Using existing images from data:", data.images);
        setExistingImages(data.images);
        setIsLoading(false);
      } else {
        // Otherwise fetch images from API
        fetchExistingImages();
      }
    } else {
      // Reset images when modal is closed or in add mode
      setExistingImages([]);
      setImagesToDelete([]);
    }
  }, [modal_center, data]);

  // Fetch existing images for this room
  const fetchExistingImages = async () => {
    if (!data.id) return;

    setIsLoading(true);
    try {
      // Create a custom axios instance for this request
      const api = axios.create({
        baseURL: process.env.REACT_APP_API_URL || "http://localhost:8000/api",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`, // Adjust based on your auth setup
        },
      });

      const response = await api.get(`/rates/images?rate_id=${data.id}`);

      if (response.data && response.data.data) {
        setExistingImages(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching existing images:", error);
      setUploadError("Failed to load existing images");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle image selection
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      setSelectedImages([...selectedImages, ...filesArray]);

      // Create preview URLs
      const newPreviewUrls = [];

      filesArray.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviewUrls.push({
            id: Math.random().toString(36).substring(2, 9),
            url: reader.result,
            file: file,
            name: file.name,
          });

          if (newPreviewUrls.length === filesArray.length) {
            setPreviewImages([...previewImages, ...newPreviewUrls]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Remove a preview image (new image)
  const removeImage = (id) => {
    const updatedPreviews = previewImages.filter(
      (preview) => preview.id !== id
    );
    setPreviewImages(updatedPreviews);

    // Also update the selectedImages array
    const removedPreview = previewImages.find((preview) => preview.id === id);
    if (removedPreview) {
      const updatedSelectedImages = selectedImages.filter(
        (file) => file.name !== removedPreview.file.name
      );
      setSelectedImages(updatedSelectedImages);
    }
  };

  // Mark existing image for deletion
  const markImageForDeletion = (imageId) => {
    setImagesToDelete([...imagesToDelete, imageId]);
  };

  // Restore image (remove from deletion list)
  const restoreImage = (imageId) => {
    setImagesToDelete(imagesToDelete.filter((id) => id !== imageId));
  };

  const onSubmit = async (formData) => {
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      // Create FormData to handle file uploads
      const formDataWithImages = new FormData();

      // Add form fields to FormData
      Object.keys(formData).forEach((key) => {
        formDataWithImages.append(key, formData[key]);
      });

      // Add images to FormData - IMPORTANT: use the correct field name
      selectedImages.forEach((file) => {
        formDataWithImages.append("images[]", file);
      });

      // Add images to delete
      imagesToDelete.forEach((imageId) => {
        formDataWithImages.append("delete_images[]", imageId);
      });

      // If editing, add the ID
      if (Object.keys(data).length !== 0) {
        formDataWithImages.append("id", data.id);
      }

      // Dispatch the action with the FormData
      if (Object.keys(data).length !== 0) {
        await dispatch(updateRates(formDataWithImages));
      } else {
        await dispatch(addRates(formDataWithImages));
      }

      // Close modal and reset state
      setmodal_center(false);
      setSelectedImages([]);
      setPreviewImages([]);
      setExistingImages([]);
      setImagesToDelete([]);
      setUploadProgress(0);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError(
        error.response?.data?.message || "Failed to upload. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal
      isOpen={modal_center}
      toggle={() => {
        tog_center();
      }}
      centered
      size="lg"
    >
      <div className="modal-header">
        <h5
          className="modal-title mt-0"
          style={{ display: "flex", alignItems: "center" }}
        >
          <i className="ri-add-circle-line" style={{ paddingRight: 5 }}></i>{" "}
          {Object.keys(data).length !== 0 ? "Update" : "Add"} Room Rate
        </h5>
        <button
          type="button"
          onClick={() => {
            setmodal_center(false);
          }}
          className="close"
          data-dismiss="modal"
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div className="modal-body">
        {/* Display upload errors if any */}
        {uploadError && <Alert color="danger">{uploadError}</Alert>}

        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3">
            <Col md="6" className="mb-2">
              <div className="field">
                <input
                  className={`form-control ${
                    errors.roomtype ? "is-invalid" : null
                  }`}
                  placeholder=""
                  type="text"
                  {...register("roomtype")}
                />
                <span className="label">Room Type</span>
                {errors.roomtype && (
                  <div className="invalid-feedback">
                    {errors.roomtype.message}
                  </div>
                )}
              </div>
            </Col>
            <Col md="6" className="mb-2">
              <div className="field">
                <input
                  className={`form-control ${
                    errors.roomtypear ? "is-invalid" : null
                  }`}
                  placeholder=""
                  type="text"
                  {...register("roomtypear")}
                />
                <span className="label">Room Type in Arabic</span>
                {errors.roomtypear && (
                  <div className="invalid-feedback">
                    {errors.roomtypear.message}
                  </div>
                )}
              </div>
            </Col>
            <Col md="6" className="mb-2">
              <div className="field">
                <input
                  className={`form-control ${
                    errors.roomfloor ? "is-invalid" : null
                  }`}
                  placeholder=""
                  type="text"
                  {...register("roomfloor")}
                />
                <span className="label">Room Floor</span>
                {errors.roomfloor && (
                  <div className="invalid-feedback">
                    {errors.roomfloor.message}
                  </div>
                )}
              </div>
            </Col>
            <Col md="6" className="mb-2">
              <div className="field">
                <input
                  className={`form-control ${
                    errors.roomfloorar ? "is-invalid" : null
                  }`}
                  placeholder=""
                  type="text"
                  {...register("roomfloorar")}
                />
                <span className="label">Room Floor in Arabic</span>
                {errors.roomfloorar && (
                  <div className="invalid-feedback">
                    {errors.roomfloorar.message}
                  </div>
                )}
              </div>
            </Col>
            <Col md="6" className="mb-2">
              <div className="field">
                <input
                  className={`form-control ${errors.syp ? "is-invalid" : null}`}
                  placeholder=""
                  type="text"
                  {...register("syp")}
                />
                <span className="label">SYP</span>
                {errors.syp && (
                  <div className="invalid-feedback">{errors.syp.message}</div>
                )}
              </div>
            </Col>
            <Col md="6" className="mb-2">
              <div className="field">
                <input
                  className={`form-control ${errors.usd ? "is-invalid" : null}`}
                  placeholder=""
                  type="text"
                  {...register("usd")}
                />
                <span className="label">USD</span>
                {errors.usd && (
                  <div className="invalid-feedback">{errors.usd.message}</div>
                )}
              </div>
            </Col>
          </Row>

          {/* Existing Images Section */}
          {Object.keys(data).length !== 0 && (
            <Row className="mb-3">
              <Col md="12">
                <div className="existing-images-section">
                  <h5 className="mb-3">Existing Images</h5>

                  {isLoading ? (
                    <div className="text-center py-3">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading existing images...</p>
                    </div>
                  ) : existingImages.length === 0 ? (
                    <Alert color="info">
                      No existing images found for this room.
                    </Alert>
                  ) : (
                    <div className="d-flex flex-wrap gap-2">
                      {existingImages.map((image) => (
                        <div
                          key={image.id}
                          className="position-relative"
                          style={{ width: "120px" }}
                        >
                          <img
                            src={image.url || "/placeholder.svg"}
                            alt={image.original_name || "Room image"}
                            className={`img-thumbnail ${
                              imagesToDelete.includes(image.id)
                                ? "opacity-50"
                                : ""
                            }`}
                            style={{
                              height: "100px",
                              objectFit: "cover",
                              width: "100%",
                            }}
                          />

                          {imagesToDelete.includes(image.id) ? (
                            <Button
                              color="success"
                              size="sm"
                              className="position-absolute top-0 end-0 rounded-circle p-1"
                              style={{ margin: "2px" }}
                              onClick={() => restoreImage(image.id)}
                              disabled={isUploading}
                              title="Restore image"
                            >
                              <i className="ri-refresh-line"></i>
                            </Button>
                          ) : (
                            <Button
                              color="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 rounded-circle p-1"
                              style={{ margin: "2px" }}
                              onClick={() => markImageForDeletion(image.id)}
                              disabled={isUploading}
                              title="Mark for deletion"
                            >
                              <i className="ri-close-line"></i>
                            </Button>
                          )}

                          <p
                            className="small text-center text-truncate mt-1"
                            style={{ maxWidth: "100%" }}
                          >
                            {imagesToDelete.includes(image.id) ? (
                              <span className="text-danger">
                                Will be deleted
                              </span>
                            ) : (
                              image.original_name || "Image"
                            )}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          )}

          {/* Image Upload Section */}
          <Row className="mb-3">
            <Col md="12">
              <div className="image-upload-section">
                <h5 className="mb-3">
                  {Object.keys(data).length !== 0
                    ? "Add New Images"
                    : "Room Images"}
                </h5>
                <div
                  className="dropzone-container border border-dashed rounded p-4 text-center cursor-pointer"
                  style={{ backgroundColor: "#f8f9fa" }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <i className="ri-upload-cloud-line display-4 text-muted"></i>
                  <p className="mt-2">Click to upload or drag and drop</p>
                  <p className="small text-muted">PNG, JPG, GIF up to 10MB</p>
                  <p className="small text-primary">
                    You can select multiple images
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="d-none"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                  />
                </div>
              </div>
            </Col>
          </Row>

          {/* Upload Progress */}
          {isUploading && (
            <Row className="mb-3">
              <Col md="12">
                <div className="d-flex justify-content-between mb-1">
                  <span>Upload Progress</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="progress" style={{ height: "20px" }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${uploadProgress}%` }}
                    aria-valuenow={uploadProgress}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </Col>
            </Row>
          )}

          {/* Preview of selected new images */}
          {previewImages.length > 0 && (
            <Row className="mb-3">
              <Col md="12">
                <h6 className="mb-2">
                  New Images to Upload ({previewImages.length})
                </h6>
                <div className="d-flex flex-wrap gap-2">
                  {previewImages.map((preview) => (
                    <div
                      key={preview.id}
                      className="position-relative"
                      style={{ width: "120px" }}
                    >
                      <img
                        src={preview.url || "/placeholder.svg"}
                        alt="Preview"
                        className="img-thumbnail"
                        style={{
                          height: "100px",
                          objectFit: "cover",
                          width: "100%",
                        }}
                      />
                      <Button
                        color="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 rounded-circle p-1"
                        style={{ margin: "2px" }}
                        onClick={() => removeImage(preview.id)}
                        disabled={isUploading}
                      >
                        <i className="ri-close-line"></i>
                      </Button>
                      <p
                        className="small text-center text-truncate mt-1"
                        style={{ maxWidth: "100%" }}
                      >
                        {preview.file.name}
                      </p>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          )}

          {/* Summary of changes (for edit mode) */}
          {Object.keys(data).length !== 0 &&
            (imagesToDelete.length > 0 || previewImages.length > 0) && (
              <Row className="mb-3">
                <Col md="12">
                  <Alert color="info">
                    <h6 className="mb-2">Summary of Changes:</h6>
                    <ul className="mb-0">
                      {imagesToDelete.length > 0 && (
                        <li>
                          {imagesToDelete.length} existing image(s) will be
                          deleted
                        </li>
                      )}
                      {previewImages.length > 0 && (
                        <li>
                          {previewImages.length} new image(s) will be uploaded
                        </li>
                      )}
                    </ul>
                  </Alert>
                </Col>
              </Row>
            )}

          <div className="d-flex flex-wrap gap-2 justify-content-center">
            <button
              type="button"
              className="btn btn-lg btn-secondary"
              onClick={() => {
                setmodal_center(false);
              }}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-lg btn-primary"
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Uploading...
                </>
              ) : Object.keys(data).length !== 0 ? (
                "Update"
              ) : (
                "Add New"
              )}
            </button>
          </div>
        </Form>
      </div>
    </Modal>
  );
}
